<template>
  <div class="view-edit-orders">
    <div v-if="loading" class="loader"></div>
    <div v-else-if="selectedOrder">
      <master-order-selector
        v-if="masterOrderSelectorOrders.length > 1"
        :key="'master-order-selector-key' + refreshMasterOrderSelector"
        :orders="masterOrderSelectorOrders"
        :enable-add-button="false"
        :selected-order-is-valid="selectedOrderIsValid"
        :enable-edit-button="false"
        :enable-clone-button="false"
        :enable-delete-button="false"
        @select-order="selectOrder">
      </master-order-selector>

      <div v-if="selectedOrder">
        <div v-if="selectedOrder.orderNumber !== orderOverviewId" class="view-edit-order-details-container">
          <ViewEditOrderDetails
            :key="'details' + selectedOrder.orderNumber"
            :order="selectedOrder"
            :edit-type="editType"
            @order-updated="updateCurrentOrder"
            @order-valid="setSelectedOrderIsValid">
          </ViewEditOrderDetails>
        </div>

        <div v-if="!isDraft" class="view-edit-order-container">
          <grid-order-shipments
            :key="'shipment' + selectedOrder.orderNumber"
            :order="selectedOrder as OrderClass"
            @shipment-selected="selectShipmentDetails" />
        </div>

        <div class="view-edit-order-container" v-if="selectedOrder">
          <div v-if="showShipmentSelector" class="item-shipment-details-selector">
            <select
              :model-value="selectedShipmentDetails"
              class="with-arrow select-packingSlip"
              @change="selectShipmentDetails">
              <option :value="selectedShipmentDetailsDefault">
                {{ $t('viewOrder.item_shipment_details_selector.all_items') }}
              </option>
              <option
                v-for="packingSlip in (selectedOrder as Order).packingSlips"
                :key="packingSlip.deliveryNumber"
                :value="packingSlip.deliveryNumber">
                {{ packingSlip.deliveryNumber }}
              </option>
            </select>
          </div>
          <grid-order-items
            :order="selectedOrder"
            :selected-shipment="selectedShipmentDetails"
            :is-draft="props.isDraft"
            :key="'grid-order-items-key' + refreshGridOrderItems" />
        </div>

        <div v-if="props.isDraft" class="splits-cnt">
          <product-split
            :key="selectedOrder.orderNumber"
            :order-numbers="orderNumbersForSplit"
            :is-draft="props.isDraft"
            :collapsable="true"
            :show-error-message="false">
          </product-split>
        </div>

        <view-order-totals v-if="!props.isDraft" :key="selectedOrder.orderNumber" :order="selectedOrder">
        </view-order-totals>

        <div v-if="!props.isDraft && isOrderInUpdateProgress(selectedOrder as Order)" class="order-update-note">
          {{ $t('viewOrder.update_order_note') }}
        </div>

        <div v-if="props.isDraft" class="terms-and-conditions" v-html="$t('viewOrder.terms_and_conditions')"></div>
      </div>
    </div>
    <div v-else class="loader"></div>
  </div>
</template>

<script setup lang="ts">
import type { OrderModel } from '@onrunning/backstage-models';
import { computed, onMounted, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRouter } from 'vue-router';

import ProductSplit from '@/components/summary/product-split.vue';
import { useFormat } from '@/composables/format';
import { useNotification } from '@/composables/useNotification';
import { useOrderManagement } from '@/composables/useOrderManagement';
import { orderOverviewId } from '@/constants/order.constants';
import type { OrderEditType } from '@/constants/orderEditType';
import { ordersMixins } from '@/mixins/orders.mixins';
import { MasterOrderSelectorModel } from '@/models/master-order-selector.model';
import { Order as OrderClass } from '@/models/order.model';
import { useOrderStore } from '@/stores/order';
import type { Draft } from '@/types/draft.interface';
import type { EditableOrder, Order, OrderEntity } from '@/types/order.interface';
import GridOrderItems, { ALL_ITEMS } from './grid-order-items.vue';
import GridOrderShipments from './grid-order-shipments.vue';
import MasterOrderSelector from './master-order-selector.vue';
import ViewEditOrderDetails from './view-edit-order-details.vue';
import ViewOrderTotals from './view-order-totals.vue';

const selectedShipmentDetailsDefault = ALL_ITEMS;

const props = defineProps<{
  id?: string;
  isDraft: boolean;
  editType?: OrderEditType;
}>();

const emit = defineEmits<{
  'orders-are-valid': [valid: boolean];
  'update-order': [orders: EditableOrder | Draft];
}>();

const { t } = useI18n();
const router = useRouter();
const { getOrders } = useOrderManagement();
const orderStore = useOrderStore();
const { notifyError } = useNotification();
const { formatDateToIsoStringIgnoringTime } = useFormat();

// Component state
const loading = ref(true);
const refreshMasterOrderSelector = ref(0);
const orderOverview = ref<OrderEntity | null>(null);
const refreshGridOrderItems = ref(0);
const selectedOrderIndex = ref<number>(0);
const updatedOrders = ref<Record<string, Draft | EditableOrder>>({});
const selectedShipmentDetails = ref(ALL_ITEMS);
const orderEditValid = ref(true);

const orderItems = computed(() => {
  const result = new Map([...orderStore.orders, ...orderStore.updatedOrders]);
  const orders = Array.from(result.values());

  return orderOverview.value ? [orderOverview.value, ...orders] : orders;
});

const selectedOrder = computed((): OrderEntity => {
  return orderItems.value[selectedOrderIndex.value];
});

const orderNumbersForSplit = computed((): string[] => {
  if (!selectedOrder.value) return [];

  if (selectedOrder.value.orderNumber === orderOverviewId) {
    return orderItems.value.filter((order) => order.orderNumber !== orderOverviewId).map((order) => order.orderNumber);
  }

  return [selectedOrder.value.orderNumber];
});

const showShipmentSelector = computed((): boolean => {
  return ((selectedOrder.value as Order)?.packingSlips ?? []).length > 0;
});

const masterOrderSelectorOrders = computed((): MasterOrderSelectorModel[] => {
  const selectorRelatedOrders = orderItems.value.map<MasterOrderSelectorModel>((order, index) => {
    const isOverview = order.orderNumber === orderOverviewId;

    return {
      id: order.orderNumber,
      title: isOverview ? t('viewOrder.overview') : formatDateToIsoStringIgnoringTime(order.dueDate),
      subTitle:
        !props.isDraft && !isOverview
          ? `${order.orderNumber}`
          : `${order.products
              .reduce((totalQuantities, product) => totalQuantities + product.quantity, 0)
              .toString()} items`,
      selected: index === selectedOrderIndex.value,
    };
  });

  return selectorRelatedOrders;
});

const selectedOrderIsValid = computed((): boolean => {
  return orderEditValid.value;
});

onMounted(() => {
  init();
});

// Methods
const init = async () => {
  loading.value = true;

  try {
    if (!orderStore.orders.size && props.id) {
      const allOrders = await getOrders(props.id, props.isDraft);

      initStore(allOrders as Order[]);
    }

    if (orderStore.orders.size > 1) {
      configureMasterOrderView();
    }
  } catch (error: any) {
    notifyError(error?.message || t('global.error'));
    goBackToOrders();
  } finally {
    loading.value = false;
  }
};

const initStore = (orders: Order[] | Draft[] | EditableOrder[]) => {
  const orderMap = new Map(orders.map((order) => [order.orderNumber, order]));

  orderStore.setOrders(orderMap);
};

const configureMasterOrderView = (): void => {
  const productMap = new Map();

  for (const order of orderStore.orders.values()) {
    for (const product of order.products) {
      const existing = productMap.get(product.productId);
      if (existing) {
        existing.quantity += product.quantity;
      } else {
        productMap.set(product.productId, { ...product });
      }
    }
  }

  orderOverview.value = {
    ...orderItems.value[0],
    orderNumber: orderOverviewId,
    products: Array.from(productMap.values()),
  };
};

const goBackToOrders = () => {
  router.push('/orders');
};

const selectShipmentDetails = (input: Event | string): void => {
  const deliveryNumber = typeof input === 'string' ? input : ((input.target as HTMLInputElement).value ?? '');
  selectedShipmentDetails.value = deliveryNumber;
};

const updateCurrentOrder = (updatedOrder: EditableOrder | Draft): void => {
  updatedOrders.value[updatedOrder.orderNumber] = updatedOrder;

  emit('update-order', updatedOrder);
};

const setSelectedOrderIsValid = (valid: boolean): void => {
  orderEditValid.value = valid;
  emit('orders-are-valid', valid);
};

const selectOrder = async (selectedIndex: number): Promise<void> => {
  selectedOrderIndex.value = selectedIndex;

  refreshGridOrderItems.value++;
};

const isOrderInUpdateProgress = (order: Order): boolean => {
  return ordersMixins.isOrderInUpdateProgress(order as OrderModel);
};
</script>

<style scoped lang="scss">
.view-edit-orders {
  .view-edit-order-container {
    @include container-background;
    margin-bottom: 20px;
    position: relative;

    .item-shipment-details-selector {
      position: absolute;
      right: 20px;
    }
  }

  .view-edit-order-details-container {
    margin-bottom: 20px;
  }

  .select-packingSlip {
    position: relative;
    right: 0;
    margin: auto;
    margin-right: 0;
  }

  .order-update-note {
    font-size: 0.9em;
    margin-top: 20px;
    float: right;
    max-width: inherit;
    text-align: right;
  }

  .terms-and-conditions {
    padding: 60px 0 0;
    line-height: 150%;
    font-size: 14px;
  }

  .splits-cnt {
    padding-top: 20px;
  }
}
</style>
